/**
 * Slice 37 — VA "Need photo" asks (nag only; never blocks cleaner).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";

export type UnitPhotoAskStatus = "open" | "fulfilled" | "dismissed";

export interface UnitPhotoAsk {
  id: string;
  cleaning_task_id: string;
  unit_item_id: string;
  requested_by: string | null;
  status: UnitPhotoAskStatus;
  fulfilled_photo_url: string | null;
  created_at: string;
  updated_at: string;
}

export function useUnitPhotoAsks(
  cleaningTaskId: string | undefined,
  opts?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: queryKeys.unitPhotoAsks.byTask(cleaningTaskId ?? ""),
    queryFn: async (): Promise<UnitPhotoAsk[]> => {
      if (!cleaningTaskId) return [];
      const { data, error } = await supabase
        .from("unit_photo_asks")
        .select("*")
        .eq("cleaning_task_id", cleaningTaskId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...row,
        status: row.status as UnitPhotoAskStatus,
      }));
    },
    enabled: !!cleaningTaskId,
    refetchInterval: opts?.refetchInterval ?? false,
  });
}

export function useCreateUnitPhotoAsk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      cleaningTaskId: string;
      unitItemId: string;
      requestedBy: string | null;
    }) => {
      const { data, error } = await supabase
        .from("unit_photo_asks")
        .upsert(
          {
            cleaning_task_id: input.cleaningTaskId,
            unit_item_id: input.unitItemId,
            requested_by: input.requestedBy,
            status: "open",
            fulfilled_photo_url: null,
          },
          { onConflict: "cleaning_task_id,unit_item_id" },
        )
        .select()
        .single();
      if (error) throw error;
      return { ...data, status: data.status as UnitPhotoAskStatus } as UnitPhotoAsk;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.unitPhotoAsks.byTask(row.cleaning_task_id),
      });
    },
  });
}

export function useUpdateUnitPhotoAsk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      cleaningTaskId: string;
      status: UnitPhotoAskStatus;
      fulfilledPhotoUrl?: string | null;
    }) => {
      const patch: {
        status: UnitPhotoAskStatus;
        fulfilled_photo_url?: string | null;
      } = { status: input.status };
      if (input.fulfilledPhotoUrl !== undefined) {
        patch.fulfilled_photo_url = input.fulfilledPhotoUrl;
      }
      const { data, error } = await supabase
        .from("unit_photo_asks")
        .update(patch)
        .eq("id", input.id)
        .select()
        .single();
      if (error) throw error;
      return { ...data, status: data.status as UnitPhotoAskStatus } as UnitPhotoAsk;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.unitPhotoAsks.byTask(row.cleaning_task_id),
      });
    },
  });
}
